import React from 'react';
import PropTypes from 'prop-types';
import { Field, Icon } from '@ontruck/orc';
import { isEmpty } from 'utils/types';
import FieldError from 'components/form/FieldError';

import { useSelector } from 'react-redux';

import AddressSelector from '../AddressSelector';
import ResponsiveModal from 'components/ResponsiveModal';
import styled, { css } from 'styled-components';
import { AddressesIcon } from 'refactored/ui/icons';
import { selectors as uiSelectors } from 'state/ui';
import { AddressFormModal } from 'components/AddressModal/container';
import {
  useSoFormStepsApi,
  useSoFormStepsState,
} from 'refactored/features/soForm/SoForm.context';
import { useTranslation } from 'react-i18next';

const LeftIconContainer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 48px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--grey100);
  svg path {
    fill: var(--grey500);
  }
`;

const RightIconContainer = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  width: 48px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) =>
    !!props.onClick &&
    css`
      cursor: pointer;
    `};
`;

const ZipCodeCityText = styled.div`
  height: 24px;
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;

  display: flex;
  align-items: center;
  text-align: right;

  color: var(--grey700);
  position: absolute;
  right: 48px;
`;

const AddressSelectorContainer = styled.div`
  width: 556px;
`;

const StopAddress = ({
  shipmentKey,
  stopType,
  responsiveModalRef,
  inputRef,
  inputModalRef,
  closeModal,
  inputValue,
  handleInputFocus,
  handleInputBlur,
  handleInputModalFocus,
  handleInputChange,
  address,
  className,
  id,
  error,
  warning,
  focused,
  handleClear,
  t,
}) => {
  const { t: catalogT } = useTranslation('Catalog');

  const stepsState = useSoFormStepsState();
  const stepsApi = useSoFormStepsApi();

  const isCreating = useSelector((state) =>
    uiSelectors.addressModalIsOpened(state, shipmentKey, stopType)
  );

  // state

  //   value
  const field = stopType === 'pickup' ? 'address_from' : 'address_to';

  //   placeholder
  const placeholder = focused
    ? catalogT('configuration.addresses.search')
    : stopType === 'pickup'
    ? catalogT('configuration.addresses.pickup_address')
    : catalogT('configuration.addresses.dropoff_address');

  //   errors
  const errors = stepsState.configuration.errors;

  const hasEmptyError = errors.fieldsWithEmptyError.includes(field);
  const showEmptyError =
    stepsState.configuration.showConfigurationErrors && hasEmptyError;

  const hasLogicError = errors.fieldsWithInvalidError.includes(field);
  const showLogicError = hasLogicError;

  const showError = showEmptyError || showLogicError;

  //   classnames

  const focusClassName = focused ? 'isFocused' : '';
  const errorClassName = showError ? 'hasError' : '';
  const totalClassName = `${className} ${errorClassName} ${focusClassName}`;

  const showClearIcon = !isEmpty(inputValue);
  const showSearchIcon = !showClearIcon && focused;

  const handleModalClose = () => {};

  const handleInputFocusWithContext = () => {
    handleInputFocus();
    stepsApi.hideNoProductsForRoute();
  };

  const handleClearWithContext = (e) => {
    handleClear(e);
    stepsApi.hideNoProductsForRoute();
  };

  return (
    <div className={totalClassName}>
      {isCreating && (
        <AddressFormModal
          shipmentKey={shipmentKey}
          stopType={stopType}
          onClose={handleModalClose}
        />
      )}
      {!!error && <FieldError className="error">{t(error)}</FieldError>}
      {!!warning && focused && (
        <FieldError className="error">{t(warning)}</FieldError>
      )}

      {/* Header is for mobile devices only */}
      <ResponsiveModal
        modalRef={responsiveModalRef}
        refProperty="fieldRef"
        inputRef={inputRef}
        disableModalOnMobile
        header={
          <Field
            type="text"
            className="stop-address__input"
            id={`${id}-modal`}
            name={id}
            fieldRef={inputModalRef}
            value={inputValue}
            placeholder={placeholder}
            label={placeholder}
            labelType="hidden"
            size="L"
            fullWidth
            onChange={handleInputChange}
            onFocus={handleInputModalFocus}
            invalid={!!warning}
            error={!!error}
            autoComplete="off"
            asideRightText={<Icon glyph="search" size="16px" color="grey500" />}
          />
        }
        content={
          <AddressSelectorContainer>
            <AddressSelector
              closeModal={closeModal}
              shipmentKey={shipmentKey}
              stopType={stopType}
              hasError={!!error}
              inputValue={inputValue}
            />
          </AddressSelectorContainer>
        }
      >
        <Field
          type="text"
          dataE2E={`${stopType}-stop-address`}
          className={`stop-address__input`}
          id={id}
          name={id}
          value={inputValue}
          placeholder={placeholder}
          label={placeholder}
          labelType="hidden"
          size="L"
          fullWidth
          onChange={handleInputChange}
          onFocus={handleInputFocusWithContext}
          onBlur={handleInputBlur}
          invalid={!!warning}
          // error={!!error || fieldHasError}
          autoComplete="off"
          asideRightText={
            address?.isZipcode ? (
              <ZipCodeCityText>{address.label}</ZipCodeCityText>
            ) : (
              <></>
            )
          }
        />
      </ResponsiveModal>

      <LeftIconContainer>
        <AddressesIcon />
      </LeftIconContainer>

      {showClearIcon && (
        <RightIconContainer onClick={handleClearWithContext}>
          <Icon glyph="close" size="16px" color="grey500" />
        </RightIconContainer>
      )}
      {showSearchIcon && (
        <RightIconContainer>
          <Icon glyph="search" size="16px" color="grey500" />
        </RightIconContainer>
      )}
    </div>
  );
};

StopAddress.propTypes = {
  placeholder: PropTypes.string,
  shipmentKey: PropTypes.string.isRequired,
  stopType: PropTypes.string.isRequired,
  responsiveModalRef: PropTypes.func,
  inputRef: PropTypes.func,
  inputModalRef: PropTypes.func,
  closeModal: PropTypes.func,
  inputValue: PropTypes.string,
  handleInputFocus: PropTypes.func.isRequired,
  handleInputModalFocus: PropTypes.func.isRequired,
  handleInputBlur: PropTypes.func.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  address: PropTypes.shape({}),
  className: PropTypes.string,
  id: PropTypes.string,
  error: PropTypes.string,
  warning: PropTypes.string,
  focused: PropTypes.bool,
  t: PropTypes.func.isRequired,
  handleClear: PropTypes.func,
};

StopAddress.defaultProps = {
  placeholder: '',
  inputValue: '',
  responsiveModalRef: () => {},
  inputRef: () => {},
  inputModalRef: () => {},
  closeModal: () => {},
  address: null,
  className: '',
  id: '',
  error: '',
  warning: '',
  focused: false,
  handleClear: () => ({}),
};

export default StopAddress;
